import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { SportsEnum } from 'types/generated/client';
import { type FindFilters, makeDefaultFilters } from 'screens/Find/filters';
import { type Neighborhood, useNeighborhoods } from 'components/Find/Neighborhoods';
import {
  DEFAULT_HOME_FILTERS,
  HomeDateRangeEnum,
  type HomeFilters,
  HomeIntentEnum,
} from './components/GetOnCourtSection/types';

/**
 * The Get-on-court bar lives in the URL, so leaving Home for a game or a lesson and
 * coming back restores what was chosen. Only what the viewer picked is written: a
 * default reads as an absent param, and the rest of the Filters sheet (venues
 * included) stays in memory.
 */
const PARAM = {
  sport: 'sport',
  intent: 'for',
  dateRange: 'when',
  neighborhoods: 'in',
} as const;

const readEnum = <T extends string>(value: unknown, allowed: readonly T[]): T | undefined =>
  allowed.find((option) => option === value);

const readSlugs = (value: unknown): string[] =>
  typeof value === 'string' ? value.split(',').filter(Boolean) : [];

export const useHomeUrlFilters = () => {
  const router = useRouter();
  const { neighborhoods: neighborhoodOptions } = useNeighborhoods();

  // The URL is the bar's state, so back and forward move it like any other navigation.
  const filters = useMemo<HomeFilters>(
    () => ({
      sport:
        readEnum(router.query[PARAM.sport], Object.values(SportsEnum)) ??
        DEFAULT_HOME_FILTERS.sport,
      intent:
        readEnum(router.query[PARAM.intent], Object.values(HomeIntentEnum)) ??
        DEFAULT_HOME_FILTERS.intent,
      dateRange:
        readEnum(router.query[PARAM.dateRange], Object.values(HomeDateRangeEnum)) ??
        DEFAULT_HOME_FILTERS.dateRange,
    }),
    [router.query],
  );

  const [sheetFilters, setSheetFilters] = useState<FindFilters>(() => makeDefaultFilters('home'));
  // The neighborhood list arrives with its own query, so the slugs resolve once it does.
  const hasPickedNeighborhoods = useRef(false);

  useEffect(() => {
    if (!router.isReady || hasPickedNeighborhoods.current || neighborhoodOptions.length === 0) {
      return;
    }
    const slugs = readSlugs(router.query[PARAM.neighborhoods]);
    if (slugs.length === 0) return;

    hasPickedNeighborhoods.current = true;
    const picked = neighborhoodOptions.filter((row) => slugs.includes(row.slug));
    if (picked.length > 0) setSheetFilters((current) => ({ ...current, neighborhoods: picked }));
  }, [router.isReady, router.query, neighborhoodOptions]);

  const writeUrl = useCallback(
    (next: HomeFilters, neighborhoods: Neighborhood[]) => {
      // Home also renders under an internal rewrite route, so the visible path comes
      // from asPath: router.pathname would swap "/" for the rewrite's own path.
      const [path, currentSearch = ''] = router.asPath.split('?');
      const query = new URLSearchParams(currentSearch);
      const set = (key: string, value: string | undefined) => {
        if (value) query.set(key, value);
        else query.delete(key);
      };

      set(PARAM.sport, next.sport === DEFAULT_HOME_FILTERS.sport ? undefined : next.sport);
      set(PARAM.intent, next.intent === DEFAULT_HOME_FILTERS.intent ? undefined : next.intent);
      set(
        PARAM.dateRange,
        next.dateRange === DEFAULT_HOME_FILTERS.dateRange ? undefined : next.dateRange,
      );
      set(
        PARAM.neighborhoods,
        neighborhoods.length > 0 ? neighborhoods.map((row) => row.slug).join(',') : undefined,
      );

      const search = query.toString();
      if (search === currentSearch) return;

      router.replace(search ? `${path}?${search}` : path, undefined, {
        shallow: true,
        scroll: false,
      });
    },
    [router],
  );

  const changeFilters = useCallback(
    (next: HomeFilters) => writeUrl(next, sheetFilters.neighborhoods),
    [writeUrl, sheetFilters.neighborhoods],
  );

  const changeSheetFilters = useCallback(
    (next: FindFilters) => {
      setSheetFilters(next);
      // Picking none again clears the param, so the next visit starts from the default market.
      hasPickedNeighborhoods.current = true;
      writeUrl(filters, next.neighborhoods);
    },
    [writeUrl, filters],
  );

  return { filters, changeFilters, sheetFilters, changeSheetFilters };
};
